import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
const webMercatorUtils = require("@arcgis/core/geometry/support/webMercatorUtils");
import Color from "@arcgis/core/Color";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as geodesicUtils from "@arcgis/core/geometry/support/geodesicUtils";
import Graphic from "@arcgis/core/Graphic";
import Geometry from "@arcgis/core/geometry/Geometry";
import Font from "@arcgis/core/symbols/Font";
import Extent from "@arcgis/core/geometry/Extent";
import Point from "@arcgis/core/geometry/Point";
import { PictureMarkerSymbol } from "@arcgis/core/symbols";
import { ref, watch, toRef, onMounted, getCurrentInstance } from "vue";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import * as labelPointOperator from "@arcgis/core/geometry/operators/labelPointOperator.js";
import { scaleForZoom } from "../composables/ZoomLevels";
import { extentOf } from "./extent";

class ArcgisFeature {
    actionType = '';
    locationDetails = {};
    editingContext = {
        originalGraphic: null,
        originalLayer: null,
        originalLabelGraphic: null,
        originalLabelLayer: null,
        originalSubLabelGraphic: null,
        originalSubLabelLayer: null,
        isEditing: false
    };
    parcelSelectionContext = {
        isActive: false,
        highlightedGraphic: null,
        selectedGraphic: null,
        params: null
    };
    originalParcelSymbols = null;
    farmFieldsLayer = null;

    constructor(view) {
        this.view = view;
        this.snappingEnabled = false;
    }

    initialSketch(params, emitter) {
        this.emitter = emitter;
        this.sketchLayer = params.sketchLayer;
        this.sketchViewModel = new SketchViewModel({
            view: params.view,
            layer: params.sketchLayer,
            polygonSymbol: new SimpleFillSymbol({
                color: new Color("rgba(51, 51,204, 0.3)"),
                style: "solid",
                outline: new SimpleLineSymbol({
                    color: new Color("rgba(255, 255, 255, 0.9)"),
                    width: 1,
                }),
            }),
            snappingOptions: {
                enabled: this.snappingEnabled,
                featureEnabled: true,
                selfEnabled: true,
                distance: 50,
                // snapLayers: the LPIS parcel layers, none when no LPIS is shown
                featureSources: [params.farmFieldsLayer, ...(params.snapLayers || [])]
                    .filter((layer) => layer && layer.declaredClass)
                    .map((layer) => ({ layer })),
            },
            defaultCreateOptions: {
                mode: "click",
            },
            // An update started by clicking a sketched graphic edits vertices only,
            // never moving the whole shape
            defaultUpdateOptions: {
                tool: "reshape",
                toggleToolOnClick: false,
                reshapeOptions: {
                    shapeOperation: "none",
                },
            },
        });

        //initialize the listeners for Sketch;
        //create
        this.sketchViewModel.on("create", (event) => {
            this.sketchHandler(event, params);
            if (event.state === "complete") {
                this.emitter.emit("sketchComplete");
            }
        });

        //update
        //this edit is only trigged when you edit drawing on the map
        this.sketchViewModel.on("update", (event) => {
            this.lasEvent = event;
            this.sketchHandlerModification(event);
        });

        // Listen for stop georeference selection event
        if (this.emitter) {
            this.emitter.on('stopGeoreferenceSelection', () => {
                if (this.activeAction === "georeference-selection" || this.activeAction === "parcel-georeference-selection") {
                    this.sketchCancel();
                    this.activeAction = null;
                    // Clear any parcel selection highlighting
                    this.clearParcelSelectionHighlight();
                }
            });
            
            // Listen for clear georeferences event
            this.emitter.on('clearGeoreferences', () => {
                this.sketchCancel();
                this.activeAction = null;
            });
        }
    }

    // Handler for parcel clicks during parcel georeference selection
    async handleParcelClick(clickedGraphic, mapPoint) {
        if (this.activeAction !== "parcel-georeference-selection") {
            return false; // Not in parcel selection mode
        }

        // Check if clicked graphic is a parcel
        if (!clickedGraphic || !clickedGraphic.attributes || clickedGraphic.attributes.type !== "fields") {
            return false; // Not a parcel, ignore click
        }

        // Check if this parcel is already used by another sample
        const parcelHashid = clickedGraphic.attributes?.hashid;
        
        if (parcelHashid && this.parcelSelectionContext.usedParcelIds && 
            this.parcelSelectionContext.usedParcelIds.includes(String(parcelHashid))) {
            // Silently ignore clicks on unavailable parcels
            return false;
        }

        // Clear any previous selection highlight
        this.clearParcelSelectionHighlight();

        // Highlight the selected parcel
        this.highlightSelectedParcel(clickedGraphic);

        // Calculate centroid and emit location
        await this.processParcelSelection(clickedGraphic);

        return true; // Click was handled
    }

    // Clear parcel selection highlighting
    clearParcelSelectionHighlight() {
        if (this.parcelSelectionContext.selectedGraphic) {
            // Restore original symbol
            const originalGraphic = this.parcelSelectionContext.selectedGraphic;
            originalGraphic.symbol = this.createMarker("fields");
            this.parcelSelectionContext.selectedGraphic = null;
        }
    }

    // Apply visual styling to unavailable parcels
    styleUnavailableParcels() {
        if (!this.parcelSelectionContext?.usedParcelIds?.length || !this.farmFieldsLayer) {
            return;
        }

        const unavailableHashids = this.parcelSelectionContext.usedParcelIds;
        
        // Store original symbols before modifying
        if (!this.originalParcelSymbols) {
            this.originalParcelSymbols = new Map();
        }
        
        // Get all graphics from the farmFieldsLayer
        const graphics = this.farmFieldsLayer.graphics.toArray();
        
        graphics.forEach(graphic => {
            const parcelHashid = graphic.attributes?.hashid;
            
            if (parcelHashid) {
                // Store original symbol if not already stored
                if (!this.originalParcelSymbols.has(parcelHashid)) {
                    this.originalParcelSymbols.set(parcelHashid, graphic.symbol?.clone());
                }
                
                // Apply styling based on availability
                if (unavailableHashids.includes(String(parcelHashid))) {
                    // Style as unavailable - grey and faded
                    graphic.symbol = new SimpleFillSymbol({
                        style: "solid",
                        color: [200, 200, 200, 0.6], // Grey with moderate opacity
                        outline: new SimpleLineSymbol({
                            color: [150, 150, 150, 0.8], // Grey outline
                            width: 1,
                            style: "dash" // Dashed to indicate unavailable
                        })
                    });
                } else {
                    // Style as available - normal green
                    graphic.symbol = new SimpleFillSymbol({
                        style: "solid",
                        color: [255, 255, 255, 0.1], // Almost transparent
                        outline: new SimpleLineSymbol({
                            color: [0, 150, 0, 0.8], // Green outline
                            width: 2,
                            style: "solid"
                        })
                    });
                }
            }
        });
        
        
    }

    // Reset parcel styling when exiting selection mode
    resetParcelStyling() {
        if (this.farmFieldsLayer && this.originalParcelSymbols) {
            const graphics = this.farmFieldsLayer.graphics.toArray();
            
            graphics.forEach(graphic => {
                const parcelHashid = graphic.attributes?.hashid;
                
                if (parcelHashid && this.originalParcelSymbols.has(parcelHashid)) {
                    // Restore original symbol
                    const originalSymbol = this.originalParcelSymbols.get(parcelHashid);
                    if (originalSymbol) {
                        graphic.symbol = originalSymbol;
                    }
                } else if (parcelHashid) {
                    // Set default symbol if no original was stored
                    graphic.symbol = new SimpleFillSymbol({
                        style: "solid",
                        color: [150, 150, 150, 0.5],
                        outline: new SimpleLineSymbol({
                            color: [36, 150, 249, 0.4],
                            width: 2,
                            style: "solid"
                        })
                    });
                }
            });
            
            // Clear stored symbols
            this.originalParcelSymbols.clear();
            this.originalParcelSymbols = null;
            
            
        }
    }

    // Highlight selected parcel
    highlightSelectedParcel(parcelGraphic) {
        // Create highlighted symbol
        const highlightSymbol = new SimpleFillSymbol({
            style: "solid",
            color: [34, 197, 94, 0.3], // Green with transparency
            outline: new SimpleLineSymbol({
                color: [34, 197, 94, 1], // Solid green outline
                width: 3,
                style: "solid",
            }),
        });

        // Apply highlight
        parcelGraphic.symbol = highlightSymbol;
        this.parcelSelectionContext.selectedGraphic = parcelGraphic;
    }

    // Process parcel selection and calculate centroid
    async processParcelSelection(parcelGraphic) {
        try {
            // Calculate centroid using the same method as parcel labeling
            const centroidPoint = labelPointOperator.execute(parcelGraphic.geometry);
            
            // Convert to lat/lng
            const centroidCoords = await this.xyToLngLat([centroidPoint.x, centroidPoint.y]);

            // Prepare location details
            this.locationDetails = {
                action: "parcel-georeference-selection",
                identifier: centroidCoords,
                identifier_xy: [centroidPoint.x, centroidPoint.y],
                identifierGraphic: parcelGraphic,
                parcelInfo: {
                    hashid: parcelGraphic.attributes?.hashid ? String(parcelGraphic.attributes.hashid) : null,
                    item_id: parcelGraphic.attributes?.item_id ? String(parcelGraphic.attributes.item_id) : null,
                    farm_id: parcelGraphic.attributes?.farm_id ? String(parcelGraphic.attributes.farm_id) : null,
                    name: this.getParcelDisplayName(parcelGraphic.attributes)
                }
            };

            // Emit the location event
            this.emitter.emit("argicsMapLocation", this.locationDetails);

        } catch (error) {
            console.error('Error processing parcel selection:', error);
        }
    }

    // Get display name for parcel
    getParcelDisplayName(attributes) {
        if (!attributes) return 'Unknown Parcel';
        
        const fieldNo = attributes.field_no || '';
        const suffix = attributes.field_no_suffix || '';
        const name = attributes.name || '';
        
        if (fieldNo && name) {
            return `${fieldNo}${suffix} - ${name}`;
        } else if (fieldNo) {
            return `${fieldNo}${suffix}`;
        } else if (name) {
            return name;
        } else {
            return 'Parcel';
        }
    }

    // this is the handler for the sketch events

    async sketchHandler(event, params) {
        await this.addAreaToDrawingGraphic(event, params);

        if (event.state != "complete" || this.lasEvent) {
            return;
        }

        // complete drawing
        let graphic = this.graphicFromEvent(event);

        let geometry = graphic.geometry.toJSON();

        //getting lat and lng from graphic
        await this.getLatAndLonFromGraphic(geometry, graphic);

        // send the location details to the parent component
        if(this.actionType == 'create-parcels-or-zones' || this.actionType == 'update-farm' || this.actionType == 'create-farm' || this.actionType == 'manual-georeferences' || this.actionType == 'georeference-selection' || this.actionType == 'parcel-georeference-selection'){
            this.emitter.emit("argicsMapLocation", this.locationDetails);
        }

        // For georeference-selection, immediately restart the point tool for multi-point selection
        if (this.activeAction === "georeference-selection") {
            // Small delay to ensure the event is processed, then restart point creation
            setTimeout(() => {
                this.sketchViewModel.create("point");
            }, 100);
        }
        
        // If we were editing, restore the original graphics after sending the update
        if (this.activeAction === "edit-parcel-and-zone" && this.editingContext.isEditing) {
            // Remove the temporary editing graphic from sketch layer
            if (this.sketchLayer && graphic) {
                this.sketchLayer.remove(graphic);
            }
            
            // The original graphics will be updated by the backend response and re-rendered
            // So we don't restore them here, just clear the editing context
            this.editingContext = {
                originalGraphic: null,
                originalLayer: null,
                originalLabelGraphic: null,
                originalLabelLayer: null,
                originalSubLabelGraphic: null,
                originalSubLabelLayer: null,
                isEditing: false
            };
        }
    }

    async getLatAndLonFromGraphic(geometry, graphic){

        let rings;

        switch (this.activeAction) {
            case "farm-location":
                this.locationDetails.action = this.activeAction;
                this.locationDetails.identifier = await this.xyToLngLat([
                    geometry.x,
                    geometry.y,
                ]);
                this.locationDetails.identifier_xy = [geometry.x, geometry.y];
                this.locationDetails.identifierGraphic = graphic;
                break;

            case "georeference-selection":
                this.locationDetails.action = this.activeAction;
                this.locationDetails.identifier = await this.xyToLngLat([
                    geometry.x,
                    geometry.y,
                ]);
                this.locationDetails.identifier_xy = [geometry.x, geometry.y];
                this.locationDetails.identifierGraphic = graphic;
                break;

            case "parcel-georeference-selection":
                // This case is handled in processParcelSelection method
                // when a parcel is clicked directly
                break;

            case "parcel-and-zone":
                this.locationDetails.action = this.activeAction;
                rings = await this.ringsXyToLngLat(
                    graphic.geometry.toJSON()?.rings[0]
                );
                this.locationDetails.identifier = rings;
                this.locationDetails.identifier_xy =
                    graphic.geometry.toJSON()?.rings[0];
                this.locationDetails.identifierGraphic = graphic;
                this.locationDetails.acre = this.calculateAcre([rings]);
                this.locationDetails.hectare = this.calculateHectare(
                    this.locationDetails.acre
                );
                break;

            case "edit-parcel-and-zone":
                this.locationDetails.action = this.activeAction;
                
                if (!graphic || !graphic.geometry) {
                    console.error('Invalid graphic or geometry for edit-parcel-and-zone');
                    break;
                }
                
                const geometryJson = graphic.geometry.toJSON();

                
                // Handle both polygon (rings) and polyline (paths) geometries
                let coords = null;
                if (geometryJson?.rings && geometryJson.rings.length > 0) {
                    coords = geometryJson.rings[0];
                } else if (geometryJson?.paths && geometryJson.paths.length > 0) {
                    coords = geometryJson.paths[0];
                }
                
                if (coords && coords.length > 0) {
                    rings = await this.ringsXyToLngLat(coords);
                    this.locationDetails.identifier = rings;
                    this.locationDetails.identifier_xy = coords;
                    this.locationDetails.identifierGraphic = graphic;
                    this.locationDetails.acre = this.calculateAcre([rings]);
                    this.locationDetails.hectare = this.calculateHectare(
                        this.locationDetails.acre
                    );
                } else {
                    console.error('No valid coordinates found in geometry for edit-parcel-and-zone', geometryJson);
                }

                break;

            default:
                break;
        }
    }

    async addAreaToDrawingGraphic(event, params) {
        // Create a TextSymbol to display the area label
        const textSymbol = new TextSymbol({
            color: new Color("white"),
            haloColor: new Color("black"),
            haloSize: "1px",
            text: "", // Will be updated dynamically with the area value
            font: {
                size: 12,
                family: "Arial",
            },
        });

            if (event.graphic || event.graphics) {
                const geometry = event?.graphic?.geometry || event?.graphics[0].geometry;

                // calculate the area of the polygon
                var area = geometryEngine.geodesicArea(geometry, "hectares");

                if (area < 0) {
                    // simplify the polygon if needed and calculate the area again
                    var simplifiedPolygon = geometryEngine.simplify(geometry);
                    if (simplifiedPolygon) {
                        area = geometryEngine.geodesicArea(
                            simplifiedPolygon,
                            "hectares"
                        );
                    }
                }

                // Update the polygon's attributes to include the area
                if(event.graphic){
                    event.graphic.attributes = {
                        ...event.graphic.attributes,
                        area: area,
                    };
                }else if(event.graphics){
                    event.graphics[0].attributes = {
                        ...event.graphics[0].attributes,
                        area: area,
                    };
                }

                // Update the text symbol with the area value
                textSymbol.text = `${area.toFixed(2)} ha`; // You can customize the label format as needed

                // Create a new graphic for the label and add it to the map view
                const labelGraphic = new Graphic({
                    geometry: geometry.centroid,
                    symbol: textSymbol,
                });

                // remove the label graphic from the map view
                params.sketchLayer.graphics.remove(this.lastAreaGraphic);

                // Add the label graphic to the map view
                params.sketchLayer.graphics.add(labelGraphic);

                this.lastAreaGraphic = labelGraphic;
            }
    }

    async sketchHandlerModification(event) {
        // get the last event and calulate all the location details
        if (event) {
            // complete drawing
            let graphic = this.graphicFromEvent(event);

            let geometry = graphic.geometry.toJSON();

            //getting lat and lng from graphic
            await this.getLatAndLonFromGraphic(geometry, graphic);

            this.emitter.emit("argicsMapLocation", this.locationDetails);
        } else {
            this.emitter.emit("argicsMapLocationUseOldDetails", {
                status: true,
            });
        }
    }


    async sketchHandlerUpdate(emitter) {
        this.emitter = emitter;
        const event = this.lasEvent;

        // get the last event and calulate all the location details
        if (event) {
            // complete drawing
            let graphic = this.graphicFromEvent(event);

            let geometry = graphic.geometry.toJSON();

            //getting lat and lng from graphic
            await this.getLatAndLonFromGraphic(geometry, graphic);

            if (this.actionType == 'update-parcels-or-zones') {
                this.emitter.emit("argicsMapLocation", this.locationDetails);
            }
            
            // If we were editing, clear the temporary graphic and context
            if (this.activeAction === "edit-parcel-and-zone" && this.editingContext.isEditing) {
                // Remove the temporary editing graphic from sketch layer
                if (this.sketchLayer && graphic) {
                    this.sketchLayer.remove(graphic);
                }
                
                // Clear editing context (don't restore original as it will be updated by backend)
                this.editingContext = {
                    originalGraphic: null,
                    originalLayer: null,
                    originalLabelGraphic: null,
                    originalLabelLayer: null,
                    originalSubLabelGraphic: null,
                    originalSubLabelLayer: null,
                    isEditing: false
                };
            }
        } else {
            this.emitter.emit("argicsMapLocationUseOldDetails", {
                status: true,
            });
        }
    }

    // this convert the xy to lng and lat
    xyToLngLat(xy) {
        return webMercatorUtils.xyToLngLat(xy[0], xy[1]);
    }

    // this convert the lng and lat to xy
    lngLatToXy(lngLat) {
        return webMercatorUtils.lngLatToXY(lngLat[0], lngLat[1]);
    }

    // this convert the rings to xy
    ringsLngLatToXy(rings) {
        const convertedRings = [];

        for (var index in rings) {
            convertedRings.push(this.lngLatToXy(rings[index]));
        }

        return convertedRings;
    }

    ringsXyToLngLat(rings) {
        const convertedRings = [];

        for (var index in rings) {
            convertedRings.push(this.xyToLngLat(rings[index]));
        }

        return convertedRings;
    }

    //
    calculateAcre(rings) {
        let acre = 0;

        try {
            let polygon = new Polygon({
                rings: rings,
            });

            let areas = geodesicUtils.geodesicAreas([polygon], "acres");
            
            if (areas && areas[0] && areas[0] > 0) {
                acre = parseFloat(areas[0].toFixed(5));
            } else if (areas && areas[0] && areas[0] < 0) {
                // Negative area means wrong winding direction, reverse the rings
                const reversedRings = rings.map(ring => [...ring].reverse());
                let reversedPolygon = new Polygon({
                    rings: reversedRings,
                });
                let reversedAreas = geodesicUtils.geodesicAreas([reversedPolygon], "acres");
                if (reversedAreas && reversedAreas[0] && reversedAreas[0] > 0) {
                    acre = parseFloat(reversedAreas[0].toFixed(5));
                }
            }
        } catch (error) {
            console.error('Error in calculateAcre:', error);
        }

        return acre;
    }

    // Static utility method to fix polygon winding direction using geodesic area calculation
    static fixPolygonWinding(coordinates) {
        if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
            return coordinates;
        }

        try {
            const fixedCoordinates = [];
            
            for (let i = 0; i < coordinates.length; i++) {
                const ring = coordinates[i];
                
                if (!ring || ring.length < 3) {
                    fixedCoordinates.push(ring);
                    continue;
                }

                // Use the exact same method as calculateAcre()
                let polygon = new Polygon({
                    rings: [ring],
                    spatialReference: { wkid: 4326 }
                });

                let areas = geodesicUtils.geodesicAreas([polygon], "acres");

                if (areas && areas.length > 0) {
                    if (areas[0] < 0) {
                        // Reverse the ring (keep first point at end too)
                        const reversedRing = [...ring].reverse();
                        // Ensure the ring is closed (first and last point are the same)
                        if (reversedRing.length > 0 && 
                            (reversedRing[0][0] !== reversedRing[reversedRing.length - 1][0] || 
                             reversedRing[0][1] !== reversedRing[reversedRing.length - 1][1])) {
                            reversedRing.push([...reversedRing[0]]);
                        }
                        
                        fixedCoordinates.push(reversedRing);
                    } else {
                        fixedCoordinates.push(ring);
                    }
                } else {
                    fixedCoordinates.push(ring);
                }
            }
            
            return fixedCoordinates;
        } catch (error) {
            console.error('Error in fixPolygonWinding:', error);
            return coordinates; // Return original on error
        }
    }

    // Remembered for future sketches and applied live to the current one,
    // so a polygon in progress keeps its vertices.
    setSnapping(enabled) {
        this.snappingEnabled = enabled;
        if (this.sketchViewModel) {
            this.sketchViewModel.snappingOptions.enabled = enabled;
        }
    }

    //This activate the sketch tool

    async activeSketchTool(params, emitter, actionAndType, graphic = null) {
        let tool;
        const {action, type} = actionAndType
        this.actionType = type;
        this.lasEvent = null;
        this.initialSketch(params, emitter);

        this.sketchCancel();

        switch (action) {
            case "farm-location":
                this.activeAction = action;
                this.sketchViewModel.create("point");
                break;

            case "georeference-selection":
                this.activeAction = action;
                this.sketchViewModel.create("point");
                break;

            case "parcel-georeference-selection":
                this.activeAction = action;
                // Don't create sketch tool - we'll handle clicks directly on parcels
                // Store parameters for parcel click detection
                this.parcelSelectionContext.isActive = true;
                this.parcelSelectionContext.params = params;
                this.parcelSelectionContext.usedParcelIds = params.usedParcelIds || [];
                
                // Store reference to the farmFieldsLayer for styling
                this.farmFieldsLayer = params.farmFieldsLayer;
                
                // Apply visual styling to unavailable parcels
                this.styleUnavailableParcels();
                break;

            case "parcel-and-zone":
                this.activeAction = action;
                this.sketchViewModel.create("polygon", { mode: "click" });
                break;

            case "edit":
                if (graphic == null) {
                    return;
                }
                
                if (action == "edit") {
                    //a field is a polygon on the farmFieldsLayer
                    this.activeAction = "edit-parcel-and-zone";
                    tool = "reshape";
                    
                    // Store editing context and hide original graphics
                    this.hideOriginalGraphicForEditing(graphic, params);

                    // Add the editing graphic to sketch layer
                    this.sketchLayer.add(graphic);

                    // Vertices only: dragging the body would move the whole parcel,
                    // and clicking it would toggle to the transform tool, which moves it too
                    this.sketchViewModel.update(graphic, {
                        tool: tool,
                        toggleToolOnClick: false,
                        reshapeOptions: {
                            shapeOperation: "none",
                        },
                    });
                }
                break;

            default:
                break;
        }
    }

            // Helper method to hide original graphic during editing
    hideOriginalGraphicForEditing(editingGraphic, params, retryCount = 0) {
        // Find and temporarily hide the original parcel graphic that matches this editing graphic
        const layers = [
            { layer: params.farmFieldsLayer || this.findLayer('farmFieldsLayer'), name: 'farmFieldsLayer' },
            { layer: params.farmSampleZoneLayer || this.findLayer('farmSampleZoneLayer'), name: 'farmSampleZoneLayer' }
        ];

        // Get the hashid from the editing graphic to match against original graphics
        // The editing graphic's hashid should match the original graphic's item_id
        const editingHashid = editingGraphic.attributes?.hashid;
        
        if (!editingHashid) {
            return;
        }

        // Count total graphics across all layers
        let totalGraphics = 0;
        for (const layerInfo of layers) {
            if (layerInfo.layer) {
                totalGraphics += layerInfo.layer.graphics.toArray().length;
            }
        }

        // If no graphics found and we haven't retried too many times, wait and retry
        if (totalGraphics === 0 && retryCount < 5) {
            setTimeout(() => {
                this.hideOriginalGraphicForEditing(editingGraphic, params, retryCount + 1);
            }, 200);
            return;
        }

        for (const layerInfo of layers) {
            if (!layerInfo.layer) {
                continue;
            }
            
            const graphics = layerInfo.layer.graphics.toArray();
            
            for (const graphic of graphics) {
                // Try to match by hashid first (preferred), then fall back to item_id
                const hashidMatch = graphic.attributes && graphic.attributes.hashid === editingHashid;
                const itemIdStrictMatch = graphic.attributes && graphic.attributes.item_id === editingHashid;
                const itemIdLooseMatch = graphic.attributes && graphic.attributes.item_id == editingHashid;
                
                if (hashidMatch || itemIdStrictMatch || itemIdLooseMatch) {
                    // Store the original graphic and remove it temporarily
                    this.editingContext.originalGraphic = graphic;
                    this.editingContext.originalLayer = layerInfo.layer;
                    this.editingContext.isEditing = true;
                    
                    layerInfo.layer.remove(graphic);
                    
                    // Also hide associated label graphics using the original graphic's attributes
                    this.hideAssociatedLabelGraphics(graphic, params);
                    
                    return;
                }
            }
        }
    }

    // Helper method to check if two geometries approximately match
    geometriesApproximatelyMatch(geom1, geom2) {
        if (!geom1 || !geom2) return false;
        
        // For polygons, compare the center points and area
        if (geom1.type === "polygon" && geom2.type === "polygon") {
            const center1 = geom1.extent?.center;
            const center2 = geom2.extent?.center;
            
            if (center1 && center2) {
                const dx = Math.abs(center1.x - center2.x);
                const dy = Math.abs(center1.y - center2.y);
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                // If centers are within 50 meters (in web mercator units), consider them a match
                return distance < 50;
            }
        }
        
        return false;
    }

    // Helper method to hide associated label graphics during editing
    hideAssociatedLabelGraphics(originalGraphic, params) {
        const labelLayers = [
            { layer: params.parcelLabelLayer || this.findLayer('parcelLabelLayer'), name: 'parcelLabelLayer' },
            { layer: params.sampleZoneLabelLayer || this.findLayer('sampleZoneLabelLayer'), name: 'sampleZoneLabelLayer' }
        ];

        // Use the original graphic's hashid and item_id to find matching labels
        const itemId = originalGraphic.attributes?.item_id;
        const hashid = originalGraphic.attributes?.hashid;
        
        if (!itemId && !hashid) return;

        for (const layerInfo of labelLayers) {
            if (!layerInfo.layer) continue;
            
            const graphics = layerInfo.layer.graphics.toArray();
            for (const graphic of graphics) {
                // Match by either hashid or item_id
                const hashidMatch = graphic.attributes && hashid && graphic.attributes.hashid === hashid;
                const itemIdMatch = graphic.attributes && itemId && graphic.attributes.item_id === itemId;
                
                if (hashidMatch || itemIdMatch) {
                    
                    if (graphic.attributes.type === 'label') {
                        this.editingContext.originalLabelGraphic = graphic;
                        this.editingContext.originalLabelLayer = layerInfo.layer;
                        layerInfo.layer.remove(graphic);
                    } else if (graphic.attributes.type === 'subTextLabel') {
                        this.editingContext.originalSubLabelGraphic = graphic;
                        this.editingContext.originalSubLabelLayer = layerInfo.layer;
                        layerInfo.layer.remove(graphic);
                    }
                }
            }
        }
    }

    // Helper method to restore original graphic after editing
    restoreOriginalGraphicAfterEditing() {
        if (this.editingContext.isEditing) {
            // Restore the original graphic
            if (this.editingContext.originalGraphic && this.editingContext.originalLayer) {
                this.editingContext.originalLayer.add(this.editingContext.originalGraphic);
            }
            
            // Restore label graphics
            if (this.editingContext.originalLabelGraphic && this.editingContext.originalLabelLayer) {
                this.editingContext.originalLabelLayer.add(this.editingContext.originalLabelGraphic);
            }
            
            if (this.editingContext.originalSubLabelGraphic && this.editingContext.originalSubLabelLayer) {
                this.editingContext.originalSubLabelLayer.add(this.editingContext.originalSubLabelGraphic);
            }
            
            // Clear editing context
            this.editingContext = {
                originalGraphic: null,
                originalLayer: null,
                originalLabelGraphic: null,
                originalLabelLayer: null,
                originalSubLabelGraphic: null,
                originalSubLabelLayer: null,
                isEditing: false
            };
        }
    }

    // Helper method to check if two geometries are approximately the same
    geometriesApproximatelyMatch(geom1, geom2) {
        if (!geom1 || !geom2) return false;
        
        // Compare geometry types
        if (geom1.type !== geom2.type) return false;
        
        // For polygons, compare the first ring's coordinates
        if (geom1.type === 'polygon' && geom2.type === 'polygon') {
            const rings1 = geom1.rings?.[0];
            const rings2 = geom2.rings?.[0];
            
            if (!rings1 || !rings2 || rings1.length !== rings2.length) return false;
            
            // Check if first few points match (within tolerance)
            const tolerance = 1; // 1 meter tolerance
            for (let i = 0; i < Math.min(3, rings1.length); i++) {
                const dx = Math.abs(rings1[i][0] - rings2[i][0]);
                const dy = Math.abs(rings1[i][1] - rings2[i][1]);
                if (dx > tolerance || dy > tolerance) return false;
            }
            
            return true;
        }
        
        return false;
    }

    // Helper method to find layer (fallback for when params don't include layer references)
    findLayer(layerName) {
        // This is a fallback - in practice, the layers should be passed in params
        if (this.view && this.view.map && this.view.map.layers) {
            return this.view.map.layers.find(layer => layer.title === layerName);
        }
        return null;
    }

    //This cancel the sketch tool

    sketchCancel() {
        let self = this;

        this.sketchLayer?.graphics.forEach(function (graphic) {
            //if we have a graphic belonging to one an existing field process it
            self.sketchLayer.remove(graphic);
        });

        if (this.view) {
            this.view.graphics.removeAll();   
        }
        if (this.sketchViewModel) {
            this.sketchViewModel.cancel();
        }
        
        // Clear parcel selection context
        this.clearParcelSelectionHighlight();
        this.resetParcelStyling();
        this.parcelSelectionContext = {
            isActive: false,
            highlightedGraphic: null,
            selectedGraphic: null,
            params: null
        };
        
        // Restore original graphics when cancelling
        this.restoreOriginalGraphicAfterEditing();
    }

    //This convert the event to graphic
    graphicFromEvent(event) {
        if (event.graphics && event.graphics.length) {
            return event.graphics[0];
        }
        return event.graphic;
    }

    addGraphic(type, coordinates, rings, attributes) {
        let graphic;
        let geometry;

        //Note: unusual issue where the this.view.spatialReference was returning the wkid previously
        //and then it stopped working. Hardcoding the wkid reference for the moment

        if (type == "identify" || type == "location") {
            geometry = new Point({
                longitude: coordinates[0],
                latitude: coordinates[1],
                spatialReference: {
                    wkid: 3857,
                },
            });
        }

        if (type == "fields" || type == "sample-zones") {
            let convertedRings = this.ringsLngLatToXy(rings[0]);
            geometry = new Polygon({
                rings: [convertedRings],
                spatialReference: {
                    wkid: 3857,
                },
            });
        }

        // Create a symbol for drawing the point
        let markerSymbol = this.createMarker(type);

        // Create a graphic and add the geometry and symbol to it
        graphic = new Graphic({
            geometry: geometry,
            symbol: markerSymbol,
            attributes: attributes,
        });

        return graphic;
    }

    clearAllLayerDrawedOnMap(layer, layersToExclude = []) {
        const layers = [
            "farmIdentifyLayer",
            "farmerLayer",
            "farmFieldsLayer",
            "farmSampleZoneLayer",
            "parcelLabelLayer",
            "sampleZoneLabelLayer",
            // "smapSamplePathsLayer",
            "parcelsLayer",
            "sketchLayer",
        ];

        layers.forEach((layerName) => {
            if (!layersToExclude.includes(layerName) && layer?.[layerName]) {
                layer[layerName].removeAll();
            }
        });
    }
    async switchMapView(farmer, layer) {
        //this allow you to clear all drawing already on the map
        this.clearAllLayerDrawedOnMap(layer, ["smapSamplePathsLayer"]);

        this.farms = farmer.farms;
        this.parcelLabelLayer = layer.parcelLabelLayer;
        this.sampleZoneLabelLayer = layer.sampleZoneLabelLayer;

        // checking if there is a farm in the farmer passed
        if (farmer == null) {
            return;
        }

        if (
            !farmer ||
            !farmer.hasOwnProperty("name") ||
            !farmer.hasOwnProperty("hashid")
        ) {
            console.warn('ArcgisFeature: Farmer missing required properties (name/hashid):', farmer);
            return;
        }

        if (!farmer.hasOwnProperty("farms")) {
            console.warn('ArcgisFeature: Farmer has no farms property, setting empty array');
            farmer.farms = [];
        }

        //used for view.goTo to farm
        this.mapZoomExtentGraphicArray = [];
        this.zoomExtentFarmerHashid = farmer.hashid;
        let vm = this;

        //add the farmer location
        const show = farmer?.hide_farm_location ?? true;
        const active_farm = farmer.active_farm;
        const check_if_active_farm_set = farmer.hasOwnProperty("active_farm");


        //checks
        const check_if_one_farm_have_location = farmer?.farms?.some((farm)=> {return farm.location || false})
        const check_if_no_farm_have_parcels = farmer?.farms?.some((farm)=> {return farm.parcels.length > 0 || false})

        farmer.farms.forEach(function (farm, farmIndex) {
            // This create a location Maker

            if (farm.location) {
                let attributes = {
                    farm_id: farm.item_id,
                    farm_hashid: farm.hashid,
                    farmer_hashid: farmer.hashid,
                    type: "location",
                };

                let locationGraphic = vm.addGraphic(
                    "location",
                    farm.location,
                    null,
                    attributes
                );
                if (show) {
                    // If an active_farm is specified, only include that farm's location in zoom extent
                    if (farmer.active_farm) {
                        if (farmer.active_farm === farm.hashid) {
                            vm.mapZoomExtentGraphicArray.push(locationGraphic);
                        }
                    } else {
                        vm.mapZoomExtentGraphicArray.push(locationGraphic);
                    }
                }

                let locationLabel = vm.addFarmLocationLabelGraphic(
                    farm,
                    farmer
                );
                layer.farmerLayer.add(locationGraphic);
                layer.farmerLayer.add(locationLabel);
            }

            // This draw the boundary of the farm
            farm.parcels.forEach(function (parcel, parcelIndex) {
                let boundaryGraphic = null;

                let attributes = {
                    farm_id: farm.item_id,
                    farmer_hashid: farmer.hashid,
                    item_id: parcel.item_id,
                    hashid: parcel.hashid,  // Add the parcel hashid for matching during editing
                };

                //if we have boundary coordinates create the graphic for it.
                if (parcel.boundaries && parcel.boundaries.length > 0) {
                    let newAttributes = {
                        ...attributes,
                        type: "fields",
                    };

                    boundaryGraphic = vm.addGraphic(
                        "fields",
                        null,
                        [parcel.boundaries],
                        newAttributes
                    );

                    // if we've create the boundary graphic add it to the layer and add it to the parcel
                    if (boundaryGraphic) {
                        vm.farms[farmIndex].parcels[
                            parcelIndex
                        ].boundaryGraphic = boundaryGraphic;
                        layer.farmFieldsLayer.add(boundaryGraphic);

                        if (check_if_active_farm_set) {
                            // Only add parcel boundaries for active farm
                            if (active_farm === farm.hashid) {
                                vm.mapZoomExtentGraphicArray.push(boundaryGraphic);
                            }
                        } else {
                            vm.mapZoomExtentGraphicArray.push(boundaryGraphic);
                        }
                    }

                    //if area hasn't been calcuated then update it
                    // if (parcel.acre == 0) {
                    let acre = vm.calculateAcre([parcel.boundaries]);
                    if (acre > 0) {
                        parcel.acre = acre;
                        vm.farms[farmIndex].parcels[parcelIndex].acre = acre;
                        //console.log('Updated parcel acre to:', acre);
                    } else {
                        //console.log('Calculated acre was 0 or invalid, keeping database value:', parcel.acre);
                    }
                    // }
                }

                vm.addLabelGraphic(
                    parcel,
                    boundaryGraphic,
                    attributes,
                    farmIndex,
                    parcelIndex,
                    "fields"
                );
            });

            farm.sample_zones.forEach(function (sampleZone, sampleZoneIndex) {
                let boundaryGraphic = null;

                let attributes = {
                    farm_id: farm.item_id,
                    farmer_hashid: farmer.hashid,
                    item_id: sampleZone.item_id,
                    hashid: sampleZone.hashid,  // Add the zone hashid for matching during editing
                    type: "sample-zones",
                };

                if (sampleZone.boundaries && sampleZone.boundaries.length > 0) {
                    boundaryGraphic = vm.addGraphic(
                        "sample-zones",
                        null,
                        [sampleZone.boundaries],
                        attributes
                    );
                    vm.farms[farmIndex].sample_zones[
                        sampleZoneIndex
                    ].boundaryGraphic = boundaryGraphic;
                    layer.farmSampleZoneLayer.add(boundaryGraphic);

                    //if area hasn't been calcuated then update it
                    // if (sampleZone.acre == 0) {
                    let acre = vm.calculateAcre([sampleZone.boundaries]);
                    if (acre > 0) {
                        sampleZone.acre = acre;
                        vm.farms[farmIndex].sample_zones[sampleZoneIndex].acre =
                            acre;
                    }
                    // }
                }

                vm.addLabelGraphic(
                    sampleZone,
                    boundaryGraphic,
                    attributes,
                    farmIndex,
                    sampleZoneIndex,
                    "sample-zones"
                );
            });
        });
        // The farmer's linked DAFM LPIS parcels count towards the extent when
        // they have already loaded (see addToFarmerExtent)
        if (this.lpisExtent?.farmerHashid === farmer.hashid) {
            vm.mapZoomExtentGraphicArray.push(this.lpisExtent.graphic);
        }

        // Include sample path graphics in the initial zoom extent calculation
        if (layer && layer.smapSamplePathsLayer && layer.smapSamplePathsLayer.graphics) {
            try {
                let addedCount = 0;
                // Add sample paths to the main zoom extent array so they're included in the single zoom operation
                layer.smapSamplePathsLayer.graphics.forEach(g => {
                    if (g && g.geometry) {
                        // ArcGIS can handle coordinate transformations automatically
                        vm.mapZoomExtentGraphicArray.push(g);
                        addedCount++;
                    }
                });

            } catch (e) {
                console.log('Unable to include sample paths in zoom extent:', e);
            }
            
            // If no sample path graphics are present yet, set up a retry mechanism
            if (layer.smapSamplePathsLayer.graphics.length === 0) {
                setTimeout(() => {
                    try {
                        const graphics = layer.smapSamplePathsLayer.graphics.toArray();
                        if (graphics.length > 0 && farmer?.zoom === true) {
                            // Add newly available sample paths and trigger a single zoom to combined extent
                            graphics.forEach(g => {
                                if (g && g.geometry) {
                                    vm.mapZoomExtentGraphicArray.push(g);
                                }
                            });
                            // Trigger zoom with all graphics (farms + sample paths)
                            if (vm.mapZoomExtentGraphicArray.length > 0) {
                                vm.goToLocation(vm.mapZoomExtentGraphicArray);
                            }
                        }
                    } catch (retryErr) {
                        console.log('Retry include sample paths failed:', retryErr);
                    }
                }, 800); // Shorter delay since we want one combined zoom
            }
        }
        // console.log(this.mapZoomExtentGraphicArray)
        // this.manageLayerVisibility('fields', layer);

        if (!farmer.is_ISEC || (farmer.is_ISEC && farmer.herd_no === null) || (check_if_one_farm_have_location && !check_if_no_farm_have_parcels)) {
            // Only zoom if explicitly enabled and not explicitly disabled
            if(farmer?.zoom === true) {
                // check if the map should zoom to the collection of laction
                this.goToLocation(this.mapZoomExtentGraphicArray);
            }
        }
    }

    addLabelGraphic(
        item,
        boundaryGraphic,
        attributes,
        farmIndex,
        parcelSampleZoneIndex,
        component
    ) {
        //add label
        let newAttributes = {
            ...attributes,
            type: "label",
        };

        let labelGraphic = null;
        let point = null;

        let font = new Font({
            size: 14,
            weight: "bold",
        });

        let textOptions = {
            color: "white",
            xoffset: 0,
            yoffset: 8,
            font: font,
        };

        if (boundaryGraphic) {
                point = labelPointOperator.execute(boundaryGraphic.geometry);
                const fieldNoWithSuffix = item.field_no + (item.field_no_suffix || '');
                textOptions.text = `${fieldNoWithSuffix+ '\n' || ""}${item.name !== null ? item.name + '\n' : ""}`;

        } else {
            return;
        }

        const text = new TextSymbol(textOptions);
        labelGraphic = new Graphic({
            geometry: point,
            symbol: text,
            attributes: newAttributes,
        });

        //add the graphic to the parcel
        if (component == "fields" || component == "identify") {
            this.farms[farmIndex].parcels[
                parcelSampleZoneIndex
            ].labelTextGraphic = labelGraphic;
            this.parcelLabelLayer.add(labelGraphic);
            
        }
        //add the graphic to the sample zone
        if (component == "sample-zones" || component == "split-sample-zone") {
            this.farms[farmIndex].sample_zones[
                parcelSampleZoneIndex
            ].labelTextGraphic = labelGraphic;
            this.sampleZoneLabelLayer.add(labelGraphic);
        }

        //add the subtext graphic showing acres/hectares
        if (boundaryGraphic) {
            this.addSubTextGraphic(
                item,
                boundaryGraphic,
                attributes,
                farmIndex,
                parcelSampleZoneIndex,
                component
            );
        }
    }

    addSubTextGraphic(
        item,
        boundaryGraphic,
        attributes,
        farmIndex,
        parcelSampleZoneIndex,
        component
    ) {
        let point = null;
        let labelSubTextGraphic = null;

        let newAttributes = {
            ...attributes,
            type: "subTextLabel",
        };

        let font = new Font({
            size: 12,
            weight: "lighter",
        });

        let yoffset = 0;
        //check if iteam have a name, if yes set yoffset to 12
        item.name != null ? yoffset = -24 : yoffset = -8

        let textOptions = {
            color: "white",
            xoffset: 0,
            yoffset: yoffset,
            font: font,
        };

        if (item.acre && item.acre > 0) {
            // Convert reactive proxy to plain array for calculateAcre
            const plainBoundaries = JSON.parse(JSON.stringify(item.boundaries));
            const acre = parseFloat(this.calculateAcre([plainBoundaries])).toFixed(2);
            textOptions.text =
              this.calculateHectare(acre) +
                " ha";
            const subtext = new TextSymbol(textOptions);
            point = labelPointOperator.execute(boundaryGraphic.geometry);
            //point = boundaryGraphic.geometry.extent.center;

            labelSubTextGraphic = new Graphic({
                geometry: point,
                symbol: subtext,
                attributes: newAttributes,
            });

            //add the graphic to the parcel
            if (component == "fields" || component == "identify") {
                this.farms[farmIndex].parcels[
                    parcelSampleZoneIndex
                ].labelSubTextGraphic = labelSubTextGraphic;
                this.parcelLabelLayer.add(labelSubTextGraphic);
            }
            //add the graphic to the sample zone
            if (
                component == "sample-zones" ||
                component == "split-sample-zone"
            ) {
                this.farms[farmIndex].sample_zones[
                    parcelSampleZoneIndex
                ].labelSubTextGraphic = labelSubTextGraphic;
                this.sampleZoneLabelLayer.add(labelSubTextGraphic);
            }
        }
    }

    calculateHectare(acre) {
        let hectare = 0;
        if (acre > 0) {
            let acreAsHectare = acre / 2.471;
            hectare = parseFloat(acreAsHectare.toFixed(2));
        }
        return hectare;
    }

    createMarker(type) {
        let symbol;

        if (type == "location") {
            symbol = new PictureMarkerSymbol({
                url: "/img/widget/icon-pin@40x40.svg",
                width: "30px",
                height: "30px",
            });
        }

        if (type == "identify") {
            symbol = new SimpleMarkerSymbol({
                style: "circle",
                color: [255, 255, 255, 1],
                size: "8px",
                outline: null,
            });
        }

        if (type == "fields") {
            let outline = new SimpleLineSymbol({
                color: [36, 150, 249, 0.4],
                width: 2,
                style: "solid",
            });

            symbol = new SimpleFillSymbol({
                style: "solid",
                color: [150, 150, 150, 0.5],
                outline: outline,
            });
        }

        if (type == "sample-zones") {
            let outline = new SimpleLineSymbol({
                color: [255, 255, 0, 1],
                width: 2,
                style: "solid",
            });

            symbol = new SimpleFillSymbol({
                style: "solid",
                color: [250, 250, 0, 0.2],
                outline: outline,
            });
        }

        if (type == "sample-zones-selected") {
            let outline = new SimpleLineSymbol({
                color: [255, 255, 0, 0.5],
                width: 1,
                style: "solid",
            });

            symbol = new SimpleFillSymbol({
                style: "solid",
                color: [250, 250, 0, 0.1],
                outline: outline,
            });
        }

        if (type == "split-sample-zone-active") {
            let outline = new SimpleLineSymbol({
                color: [255, 120, 0, 0.8],
                width: 2,
                style: "solid",
            });

            symbol = new SimpleFillSymbol({
                style: "solid",
                color: [250, 120, 0, 0.2],
                outline: outline,
            });
        }

        return symbol;
    }

    addFarmLocationLabelGraphic(farm, farmer) {
        let labelGraphic = null;

        let attributes = {
            farm_id: farm.item_id,
            farm_hashid: farm.hashid,
            farmer_hashid: farmer.hashid,
            type: "location-label",
        };

        let font = new Font({
            size: 14,
            weight: "lighter",
        });

        let textOptions = {
            color: "white",
            xoffset: 0,
            yoffset: -24,
            font: font,
        };

        let point = new Point({
            longitude: farm.location[0],
            latitude: farm.location[1],
            spatialReference: {
                wkid: 3857,
            },
        });

        textOptions.text = farm.name;
        const subtext = new TextSymbol(textOptions);

        labelGraphic = new Graphic({
            geometry: point,
            symbol: subtext,
            attributes: attributes,
        });

        return labelGraphic;
    }

    manageLayerVisibility(component, layer) {
        if (component == "fields") {
            layer.farmIdentifyLayer.visible = true;
            // Don't force farmFieldsLayer visibility - let the layer toggle control this
            // layer.farmFieldsLayer.visible = true;
            layer.farmSampleZoneLayer.visible = false;
            // Let scale-based visibility handle label layers automatically
        }

        if (component == "identify") {
            layer.farmIdentifyLayer.visible = true;
            layer.farmFieldsLayer.visible = false;
            layer.farmSampleZoneLayer.visible = false;
            // Let scale-based visibility handle label layers automatically
        }

        if (component == "sample-zones") {
            layer.farmIdentifyLayer.visible = false;
            layer.farmFieldsLayer.visible = true;
            layer.farmSampleZoneLayer.visible = true;
            layer.farmerLayer.visible = true;
            // Let scale-based visibility handle label layers automatically
        }
    }

    /**
     * Adds a farmer's linked DAFM LPIS parcels to that farmer's zoom extent,
     * kept as one extent graphic. When the farmer's zoom has already been
     * worked out, the extent joins it straight away, and zoom: true zooms again.
     * Otherwise switchMapView picks it up.
     */
    addToFarmerExtent(farmerHashid, graphics, { zoom = false } = {}) {
        const previous = this.lpisExtent?.graphic;
        const extent = extentOf(graphics.map((graphic) => graphic.geometry));
        this.lpisExtent = extent ? { farmerHashid, graphic: new Graphic({ geometry: extent }) } : null;

        if (this.zoomExtentFarmerHashid !== farmerHashid || !this.mapZoomExtentGraphicArray) return;
        this.mapZoomExtentGraphicArray = this.mapZoomExtentGraphicArray.filter((graphic) => graphic !== previous);
        if (!this.lpisExtent) return;
        this.mapZoomExtentGraphicArray.push(this.lpisExtent.graphic);
        if (zoom) this.goToLocation(this.mapZoomExtentGraphicArray);
    }

    goToLocation(target) {
        // Check if view is ready before attempting goTo
        if (!this.view || !this.view.ready) {
            // Wait for view to be ready, then retry
            if (this.view) {
                this.view.when(() => {
                    this.goToLocation(target);
                }).catch(e => console.log('goToLocation: View ready wait failed:', e));
            }
            return;
        }
        
        // Fixed scale ONLY for point targets; extents/polygons use expand.
        const goToOptions = { duration: 600, easing: 'in-out-cubic', padding: { top: 40, right: 40, bottom: 40, left: 40 } };
        // Standard web-mercator level 16 as a scale (lower level = zoomed out more context).
        const POINT_SCALE = scaleForZoom(16);

        try {
            // [x, y] center array
            if (Array.isArray(target) && target.length === 2 && typeof target[0] === 'number') {
                this.view.goTo({ center: target, scale: POINT_SCALE }, goToOptions).catch(e => console.log('goTo center array error:', e));
                return;
            }

            // Array of graphics/geometries (multi-target -> combined extent with expand)
            if (Array.isArray(target) && target.length > 0) {
                const graphics = target.filter(g => g && (g.geometry || g.type));
                if (!graphics.length) return;
                if (graphics.length === 1) {
                    target = graphics[0];
                } else {
                    // Build a union extent and apply spatial expand
                    let unionExtent = null;
                    const targetSpatialRef = this.view.spatialReference;
                    
                    for (const g of graphics) {
                        const geom = g.geometry;
                        if (!geom) continue;
                        
                        // Allow Web Mercator variants (3857, 102100) to be combined
                        // These are the same coordinate system with different WKID codes
                        if (geom.spatialReference && targetSpatialRef) {
                            const geomWkid = geom.spatialReference.wkid || geom.spatialReference.latestWkid;
                            const targetWkid = targetSpatialRef.wkid || targetSpatialRef.latestWkid;
                            
                            const webMercatorCodes = [3857, 102100];
                            const geomIsWebMercator = webMercatorCodes.includes(geomWkid);
                            const targetIsWebMercator = webMercatorCodes.includes(targetWkid);
                            
                            // Skip only if they're truly incompatible coordinate systems
                            if (!geomIsWebMercator || !targetIsWebMercator) {
                                continue;
                            }
                        }
                        
                        let gExtent = geom.extent;
                        if (!gExtent && geom.type === 'point') {
                            // Tiny buffer around point so it contributes
                            const sz = 25; // map units (~meters in WebMercator near equator)
                            gExtent = new Extent({
                                xmin: geom.x - sz,
                                ymin: geom.y - sz,
                                xmax: geom.x + sz,
                                ymax: geom.y + sz,
                                spatialReference: geom.spatialReference || targetSpatialRef
                            });
                        }
                        if (!gExtent) continue;
                        
                        try {
                            unionExtent = unionExtent ? unionExtent.union(gExtent) : gExtent;
                        } catch (extentError) {
                            console.log('Error combining extents, skipping geometry:', extentError);
                            continue;
                        }
                    }
                    if (unionExtent) {
                        // Adjust factor as needed (1.10 = 10% breathing room)
                        const expanded = unionExtent.expand(1.10);
                        this.view.goTo(expanded, goToOptions).catch(e => console.log('goTo combined extent error:', e));
                    } else {
                        // Fallback: original behavior
                        this.view.goTo(graphics, goToOptions).catch(e => console.log('goTo graphics array error:', e));
                    }
                    return;
                }
            }

            // Graphic or Geometry
            if (target && target.geometry) {
                const geom = target.geometry;
                if (geom.type === 'point') {
                    this.view.goTo({ center: geom, scale: POINT_SCALE }, goToOptions).catch(e => console.log('goTo point graphic error:', e));
                    return;
                }
                if (geom.extent) {
                    this.view.goTo(geom.extent.expand(1.10), goToOptions).catch(e => console.log('goTo polygon extent error:', e));
                    return;
                }
                // Fallback direct geometry
                this.view.goTo(geom, goToOptions).catch(e => console.log('goTo geometry fallback error:', e));
                return;
            }

            // Plain extent-like object
            if (target && target.xmin !== undefined && target.xmax !== undefined) {
                const extentObj = new Extent(target).expand(1.20);
                this.view.goTo(extentObj, goToOptions).catch(e => console.log('goTo extent-like error:', e));
                return;
            }

            // Viewpoint-style object
            if (target && (target.center || target.scale || target.zoom)) {
                // If it's a point center with no zoom or scale set, add our point scale. Otherwise leave as-is.
                if (!target.zoom && !target.scale && target.center && Array.isArray(target.center) && target.center.length === 2) {
                    target.scale = POINT_SCALE;
                }
                this.view.goTo(target, goToOptions).catch(e => console.log('goTo viewpoint error:', e));
                return;
            }

            // Fallback direct
            if (target) {
                this.view.goTo(target, goToOptions).catch(e => console.log('goTo fallback error:', e));
            }
        } catch (err) {
            console.log('goToLocation unexpected error (zoom-based):', err);
        }
    }
}

export default ArcgisFeature;
